/**
 * Every sentence the plan and usage system says to a person, in one file.
 *
 * The header chip, the dashboard banner and the plan page all read from here, so the three
 * can never word the same situation differently, and a copy review touches one file instead
 * of six components.
 *
 * `bannerFor` decides which single thing is worth saying right now. Only one banner is ever
 * shown: a person who is out of storage does not also need to be told they are at 80 per
 * cent of it.
 */
import { formatDate } from '@/http/requests/account';

const DAY = 24 * 60 * 60 * 1000;
const FIRST_SEEN_KEY = 'decastFirstSeen';
const DISMISSED_KEY = 'decastBannerDismissed';
const WELCOME_DAYS = 7;

/** When this browser first saw an account summary. Used only to stop welcoming forever. */
function firstSeen() {
  try {
    const raw = localStorage.getItem(FIRST_SEEN_KEY);
    if (raw) return Number(raw);
    const now = Date.now();
    localStorage.setItem(FIRST_SEEN_KEY, String(now));
    return now;
  } catch (e) {
    return Date.now();
  }
}

function dismissedMap() {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
}

/**
 * A dismissal is remembered against the exact situation, so crossing from a warning into a
 * block, or moving into a new billing period, speaks up again.
 */
export function isDismissed(banner) {
  if (!banner || !banner.dismissible) return false;
  const until = dismissedMap()[banner.dismissKey];
  return !!until && Date.now() < until;
}

export function dismiss(banner) {
  if (!banner || !banner.dismissible) return;
  try {
    const map = dismissedMap();
    map[banner.dismissKey] = Date.now() + (banner.dismissDays || 1) * DAY;
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(map));
  } catch (e) {
    // a browser that refuses storage simply shows the banner again; nothing breaks
  }
}

const UPGRADE = { label: 'See plans', to: '/dashboard/account' };
const RECORDINGS = { label: 'Manage recordings', to: '/dashboard/casts' };
const BILLING = { label: 'Update payment method', action: 'portal' };

/**
 * The one thing worth saying, or null when everything is fine.
 *
 * Order matters and is deliberate: what is blocking now, then what is about to, then what is
 * merely worth knowing.
 */
export function bannerFor(acc) {
  if (!acc) return null;
  const period = acc.periodEnd || '';

  // 1. the account is paid for but the card failed: nothing is blocked, but it will be
  if (acc.status === 'past_due') {
    return {
      key: 'past_due',
      tone: 'bad',
      title: 'Your last payment failed.',
      text: `Update your card to keep ${acc.planName}. Nothing has changed yet.`,
      actions: [BILLING],
      dismissible: false,
    };
  }

  // 2 and 3. at a limit: new work is refused, everything already made still works
  if (acc.storage.tone === 'over') {
    return {
      key: `storage_over:${acc.tier}`,
      tone: 'bad',
      title: `Storage is full: ${acc.storage.usedLabel} of ${acc.storage.allowedLabel}.`,
      text: 'New sessions will not be recorded. Everything you have stays available.',
      actions: [UPGRADE, RECORDINGS],
      dismissible: false,
    };
  }
  if (acc.ai.tone === 'over') {
    return {
      key: `ai_over:${period}`,
      tone: 'bad',
      title: `AI hours are spent: ${acc.ai.usedLabel} of ${acc.ai.allowedLabel}.`,
      text: period
        ? `New AI work resumes ${formatDate(period)}.`
        : 'New AI work resumes next period.',
      actions: [UPGRADE],
      dismissible: false,
    };
  }

  // 5 and 6. close to a limit: the warning that has to arrive before any block
  if (acc.storage.tone === 'warn') {
    return {
      key: `storage_warn:${acc.tier}`,
      tone: 'warn',
      title: `You have used ${acc.storage.usedLabel} of ${acc.storage.allowedLabel} storage.`,
      text: 'New sessions stop being recorded when it is full.',
      actions: [UPGRADE, RECORDINGS],
      dismissible: true,
      dismissKey: `storage_warn:${acc.tier}`,
      dismissDays: 3,
    };
  }
  if (acc.ai.tone === 'warn') {
    return {
      key: `ai_warn:${period}`,
      tone: 'warn',
      title: `You have used ${acc.ai.usedLabel} of ${acc.ai.allowedLabel} AI hours.`,
      text: period
        ? `They reset ${formatDate(period)}.`
        : 'They reset next period.',
      actions: [UPGRADE],
      dismissible: true,
      dismissKey: `ai_warn:${period}`,
      dismissDays: 3,
    };
  }

  // 7. brand new and on the free tier: say what they have, once, for a week
  if (!acc.paying && Date.now() - firstSeen() < WELCOME_DAYS * DAY) {
    return {
      key: 'welcome',
      tone: 'info',
      title: `You are on ${acc.planName}, free forever.`,
      text: `It includes ${acc.storage.allowedLabel} of storage and ${acc.ai.allowedLabel} of AI video a month.`,
      actions: [
        { label: 'See what each plan includes', to: '/dashboard/account' },
      ],
      dismissible: true,
      dismissKey: 'welcome',
      dismissDays: WELCOME_DAYS,
    };
  }

  return null;
}

/** The plan card's status pill and the line under the plan name. */
export function planStatus(acc) {
  if (!acc) return { label: '', tone: 'neutral', line: '' };
  if (acc.status === 'past_due') {
    return {
      label: 'Payment failed',
      tone: 'bad',
      line: 'Your last payment failed. Update your card to keep this plan.',
    };
  }
  if (acc.status === 'trialing') {
    return { label: 'Trial', tone: 'good', line: 'You are on a trial.' };
  }
  if (acc.paying) {
    return { label: 'Active', tone: 'good', line: 'Thanks for subscribing.' };
  }
  return {
    label: 'Free',
    tone: 'neutral',
    line: 'Free forever, with provenance built in.',
  };
}
